import React from "react";

import { BIKE_STATION_POPUP_CONFIG } from "../../../config/bikeStationPopup.configuration";
import { BIKE_POPUP_CHIP_CLASS_NAME } from "./bikeStationPopupModern.layout.constants";
import type { BikeStationPopupTheme } from "./bikeStationPopupModern.theme";
import { getCapacityFillRatio } from "./bikeStationPopupModern.tools";

interface BikeStationPopupLoadingSkeletonProps {

    /** Number of bikes known from the map or preload (drives capacity skeleton when available). */
    "bikesAvailable"?: number | null;

    /** Station capacity known from the map or preload. */
    "capacity"?: number | null;

    /** Number of vehicle chip rows to show. */
    "chipRowCount"?: number;

    /** Popup theme tokens. */
    "theme": BikeStationPopupTheme;
}

interface SkeletonBarProps {

    /** Extra classes. */
    "className"?: string;

    /** Inline styles. */
    "style"?: React.CSSProperties;
}

interface VehicleChipSkeletonProps {

    /** Whether this row should mimic an electric bike chip with a battery + arrow. */
    "hasBattery"?: boolean;

    /** Whether this row should show a trailing arrow like a clickable chip. */
    "hasArrow"?: boolean;

    /** Row index for width variation. */
    "rowIndex": number;

    /** Popup theme tokens. */
    "theme": BikeStationPopupTheme;
}

/**
 * Small placeholder bar reused for labels and counts.
 *
 * @param props Bar props.
 * @returns A rounded placeholder bar.
 */
const PlaceholderBar: React.FC<SkeletonBarProps> = ( {
    className = "",
    style,
} ) => (
    <div
        className={`rounded ${ className }`}
        style={style}
    />
);

/**
 * Skeleton for the big count / availability label shown when no capacity bar is used.
 *
 * @param theme Popup theme tokens.
 * @returns Count row placeholder.
 */
const CountSectionSkeleton: React.FC<{ "theme": BikeStationPopupTheme }> = ( { theme } ) => (
    <div className="flex items-center justify-between">
        <PlaceholderBar
            className="h-8 w-16"
            style={{
                "backgroundColor": theme.mutedText,
                "opacity":         0.45,
            }}
        />
        <PlaceholderBar
            className="h-5 w-36"
            style={{
                "backgroundColor": theme.mutedText,
                "opacity":         0.3,
            }}
        />
    </div>
);

/**
 * Skeleton for the capacity bar shown at the top of the popup body.
 *
 * @param theme Popup theme tokens.
 * @returns Capacity bar placeholder.
 */
const CapacityBarSkeleton: React.FC<{ "theme": BikeStationPopupTheme }> = ( { theme } ) => (
    <div
        className="relative h-[1.5rem] w-full rounded-full overflow-hidden my-3"
        style={{
 "backgroundColor": theme.mutedText,
"opacity":         0.15,
}}
    >
        <div
            className="absolute left-0 top-0 h-full rounded-full"
            style={{
                "backgroundColor": theme.mutedText,
                "opacity":         0.35,
                "width":           "55%",
            }}
        />
        <div className="absolute inset-0 z-10 flex items-center justify-center">
            <PlaceholderBar
                className="h-3 w-28"
                style={{
                    "backgroundColor": theme.mutedText,
                    "opacity":         0.6,
                }}
            />
        </div>
    </div>
);

/**
 * Single vehicle chip skeleton, shaped like the real `BIKE_POPUP_CHIP_CLASS_NAME` chip.
 *
 * The layout mirrors the loaded chip: icon, count + label, then optional battery/range/arrow block.
 *
 * @param props Chip skeleton props.
 * @returns One skeleton chip row.
 */
const VehicleChipSkeleton: React.FC<VehicleChipSkeletonProps> = ( {
    hasArrow = false,
    hasBattery = false,
    rowIndex,
    theme,
} ) => (
    <div
        aria-hidden
        className={BIKE_POPUP_CHIP_CLASS_NAME}
        style={{
            "backgroundColor": theme.chipBackground,
            "borderColor":     theme.chipBorder,
        }}
    >
        <div
            className="h-6 w-6 rounded-full flex-shrink-0"
            style={{
                "backgroundColor": theme.mutedText,
                "opacity":         0.35,
            }}
        />
        <PlaceholderBar
            className="h-4 flex-1"
            style={{
                "backgroundColor": theme.mutedText,
                "opacity":         0.45,
                "maxWidth":        rowIndex % 2 === 0 ? "45%" : "55%",
            }}
        />
        <div className="ml-auto flex items-center gap-1.5">
            {hasBattery && (
                <div className="flex flex-col items-center gap-0.5">
                    <div
                        className="h-5 w-5 rounded flex-shrink-0"
                        style={{
                            "backgroundColor": theme.mutedText,
                            "opacity":         0.35,
                        }}
                    />
                    <PlaceholderBar
                        className="h-3 w-10"
                        style={{
                            "backgroundColor": theme.mutedText,
                            "opacity":         0.3,
                        }}
                    />
                </div>
            )}
            {hasArrow && (
                <div
                    className="h-3.5 w-2.5 rounded flex-shrink-0"
                    style={{
                        "backgroundColor": theme.mutedText,
                        "opacity":         0.35,
                    }}
                />
            )}
        </div>
    </div>
);

/**
 * Loading skeleton for the modern bike station popup, shaped like the loaded interface.
 *
 * Renders a count or capacity bar placeholder followed by vehicle chip placeholders.
 *
 * @param props Skeleton props.
 * @returns Skeleton placeholder matching the popup summary layout.
 */
export const BikeStationPopupLoadingSkeleton: React.FC<BikeStationPopupLoadingSkeletonProps> = ( {
    bikesAvailable,
    capacity,
    chipRowCount = 2,
    theme,
} ) => {
    const fillRatio = getCapacityFillRatio( bikesAvailable, capacity );
    const showCapacityBar = BIKE_STATION_POPUP_CONFIG.showCapacityBar && fillRatio != null && capacity != null;

    const chips = Array.from( { "length": chipRowCount }, ( _, rowIndex ) => {
        const isEbikeRow = rowIndex === 0;
        const isBikeRow = rowIndex === 1;

        return (
            <VehicleChipSkeleton
                hasArrow={isEbikeRow || isBikeRow}
                hasBattery={isEbikeRow}
                key={`bike-popup-skeleton-row-${ rowIndex }`}
                rowIndex={rowIndex}
                theme={theme}
            />
        );
    } );

    return (
        <div
            aria-hidden
            className="py-3 pt-0 px-3 flex flex-col flex-1"
        >
            {showCapacityBar
                ? <CapacityBarSkeleton theme={theme} />
                : <CountSectionSkeleton theme={theme} />}
            <div className="flex flex-col gap-1.5">
                {chips}
            </div>
        </div>
    );
};

export default BikeStationPopupLoadingSkeleton;
